/**
 * useWorkspaceSwitcher
 *
 * Hook for switching the active workspace. Handles:
 * - Updating the workspace Zustand store
 * - Invalidating all workspace-scoped React Query queries
 * - Navigation to dashboard after switch
 */

import { useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspaceStore } from '../stores/workspaceStore';
import type { Workspace, WorkspaceMemberRole } from '@mellify/shared';
import { ROUTES } from '../routes/routeConfig';

/**
 * Workspace-scoped query key prefixes that should be invalidated on workspace switch.
 */
const WORKSPACE_SCOPED_QUERY_PREFIXES = [
  'posts',
  'campaigns',
  'calendar',
  'media',
  'inbox',
  'mentions',
  'analytics',
  'knowledge',
  'settings',
  'social-accounts',
  'engagement',
  'notifications',
  'reports',
  'members',
  'brand-voice',
  'personas',
  'approval-workflows',
  'agent-configurations',
] as const;

export interface UseWorkspaceSwitcherReturn {
  /** Switch to a different workspace */
  switchWorkspace: (workspace: Workspace, role?: WorkspaceMemberRole | null) => Promise<void>;
  /** Whether a switch is in progress */
  isSwitching: boolean;
}

export function useWorkspaceSwitcher(): UseWorkspaceSwitcherReturn {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setCurrentWorkspace, setCurrentMemberRole, isLoading } = useWorkspaceStore();

  const switchWorkspace = useCallback(
    async (workspace: Workspace, role?: WorkspaceMemberRole | null) => {
      const currentWorkspace = useWorkspaceStore.getState().currentWorkspace;

      // Skip if already on this workspace
      if (currentWorkspace?.id === workspace.id) return;

      // Update the store (this persists to localStorage automatically)
      setCurrentWorkspace(workspace);
      if (role !== undefined) {
        setCurrentMemberRole(role);
      }

      // Invalidate all workspace-scoped queries so they refetch with the new workspace context
      await Promise.all(
        WORKSPACE_SCOPED_QUERY_PREFIXES.map((prefix) =>
          queryClient.invalidateQueries({ queryKey: [prefix] })
        )
      );

      // Also remove any stale queries to prevent showing old workspace data
      WORKSPACE_SCOPED_QUERY_PREFIXES.forEach((prefix) => {
        queryClient.removeQueries({ queryKey: [prefix], type: 'inactive' });
      });

      // Navigate to dashboard root to avoid stale workspace-specific pages
      navigate(ROUTES.DASHBOARD, { replace: true });
    },
    [setCurrentWorkspace, setCurrentMemberRole, queryClient, navigate]
  );

  return {
    switchWorkspace,
    isSwitching: isLoading,
  };
}